import { AbstractCommand } from '../AbstractCommand';

import type { KaraokeClip } from '../../models/karaoke/KaraokeClip';
import type { VideoEditor } from '../../VideoEditor';

export class HideInKaraokeCommand extends AbstractCommand {
  private transcriptTokenIds: string[];
  private backedUpClipHiddenTokenIds: Map<
    string,
    KaraokeClip['hiddenTranscriptTokenIds']
  > = new Map();

  constructor(editor: VideoEditor, transcriptTokenIds: string[]) {
    super(editor);
    this.transcriptTokenIds = transcriptTokenIds;
  }

  execute(): boolean {
    if (!this.transcriptTokenIds) return false;

    const track = this.editor.getKaraokeTrack();
    if (!track) return false;

    const clips = track.karaokeClips;
    if (clips.length === 0) return false;

    let didModify = false;
    for (let i = 0; i < clips.length; i++) {
      const clip = clips[i];
      const clipTokensToHide = this.getClipTokensToHide(clip);
      if (clipTokensToHide.length) {
        this.backupClipHiddenTokenIds(clip);
        this.updateClipHiddenTokenIdsWith(clip, clipTokensToHide);
        didModify = true;
      }
    }

    if (!didModify) return true; // structural no-op success

    this.editor.markDirty('hide_in_karaoke');
    return true;
  }

  undo(): boolean {
    const track = this.editor.getKaraokeTrack();
    if (!track) return false;

    const clips = track.karaokeClips;
    if (clips.length === 0) return false;

    const indexById = new Map<string, number>();
    for (let i = 0; i < clips.length; i++) {
      indexById.set(clips[i].id, i);
    }

    for (const [clipId, hiddenTranscriptTokenIds] of this
      .backedUpClipHiddenTokenIds) {
      const index = indexById.get(clipId);
      if (index !== undefined && clips[index]) {
        clips[index].setHiddenTranscriptTokenIds(hiddenTranscriptTokenIds);
      }
    }

    this.editor.markDirty('hide_in_karaoke_undo');
    return true;
  }

  getName(): string {
    return 'HideInKaraokeCommand';
  }

  getDescription(): string {
    return 'Hides specified transcript tokens within a karaoke clip';
  }

  private getClipTokensToHide(clip: KaraokeClip): string[] {
    const tokensToHideSet = new Set(this.transcriptTokenIds);
    return clip.transcriptTokens
      .filter(t => tokensToHideSet.has(t.id))
      .map(t => t.id);
  }

  private updateClipHiddenTokenIdsWith(
    clip: KaraokeClip,
    clipTokensToHide: string[],
  ): void {
    // make sure previously hidden tokens are not erased
    clip.setHiddenTranscriptTokenIds([
      ...new Set([...clip.hiddenTranscriptTokenIds, ...clipTokensToHide]),
    ]);
  }

  private backupClipHiddenTokenIds(clip: KaraokeClip): void {
    this.backedUpClipHiddenTokenIds.set(clip.id, [
      ...clip.hiddenTranscriptTokenIds,
    ]);
  }
}
